// Curated map seeds. Custom Battle and the multiplayer lobby no longer roll a fully random world (nor
// reuse one page-load seed for every match of a session, which made "new game" feel like the same map):
// each (mapKind x size) ships THREE hand-picked, named boards, and match creation draws one at random.
// Every seed here was vetted by scripts/seedhunt.ts (validity, capital fairness, an all-AI playout) and
// eyeballed in the ?maplab gallery; the trio per pool is chosen for CHARACTER spread, one open board,
// one middling, one craggy/slow, so variety is guaranteed instead of hoped for.
//
// Pure data + a picker. No engine imports beyond types, safe for main.ts and the MP host alike (the
// host draws the seed and ships it in MatchSettings, so clients never consult this table mid-match).
import type { MapKind } from './types';

export interface CuratedSeed {
  seed: number;
  name: string; // shown in the maplab gallery (and anywhere else we ever surface the board's identity)
  blurb: string; // one line of character, for the gallery card
  heart?: boolean; // this board carries the neutral HEART ISLE prize at the centre of the wheel
}

/** Pools keyed `${mapKind}/${islandSize}`. Sizes match the setup screen's Small/Medium/Large. */
export const MAP_SEEDS: Record<string, readonly CuratedSeed[]> = {
  // EVERY isles board carries the neutral centre isle (the prize in the middle); the trio still spreads
  // home-island character: open, craggy, and a board named for the heart itself.
  'isles/8': [
    { seed: 1374494713, name: 'The Middle Sea', blurb: 'Open islands and an even ring: the classic fight.', heart: true },
    { seed: 1374494898, name: 'Spearpoint Crags', blurb: 'Every island keeps one cliff: fight around the stone.', heart: true },
    { seed: 1374495786, name: 'Heartwater', blurb: 'A wild fourth isle sits between the three of you. Take it.', heart: true },
  ],
  'isles/10': [
    { seed: 1374496528, name: 'Open Waters', blurb: 'Flat islands, clean shores, pure strategy.', heart: true },
    { seed: 1374497453, name: 'Cragfall Isles', blurb: 'Cliff-bitten islands where position is everything.', heart: true },
    { seed: 1374497638, name: 'The Prize Isle', blurb: 'A rough neutral island at the centre, open to every fleet.', heart: true },
  ],
  'isles/12': [
    { seed: 1374498676, name: 'The Grand Ring', blurb: 'Open crowns on a wide sea.', heart: true },
    { seed: 1374499527, name: "Titan's Teeth", blurb: 'The craggiest archipelago on the charts.', heart: true },
    { seed: 1374499823, name: 'Crown of the Sea', blurb: 'The centre isle belongs to nobody. It will belong to someone.', heart: true },
  ],
  'land/8': [
    { seed: 1374495342, name: 'The Open Marches', blurb: 'Low hills and long sightlines: armies meet in the middle.' },
    { seed: 1374494787, name: "King's Crossroads", blurb: 'Ridges pinch the old roads into contested crossings.' },
    { seed: 1374495860, name: 'Stonefield', blurb: 'A broken upland where every pass is a chokepoint.' },
  ],
  'land/10': [
    { seed: 1374497083, name: 'Greenvale', blurb: 'A broad heartland, a few hills, lakes on the marches.' },
    { seed: 1374497823, name: 'The Lake Marches', blurb: 'Lakes carve the middle: hold the shores between them.' },
    { seed: 1374497712, name: 'The Shattered Spine', blurb: 'A mountain chain splits the realm in two.' },
  ],
  'land/12': [
    { seed: 1374499157, name: 'The Broad Realm', blurb: 'A vast continent, lightly ridged, for long wars.' },
    { seed: 1374499453, name: 'The Old Dominion', blurb: 'Highlands and lakes break the land into provinces.' },
    { seed: 1374498639, name: 'Worldspine', blurb: 'A wall of mountains rules every march.' },
  ],
};

/** The pool for a configuration (falls back to the nearest size so future sizes never crash). */
export function seedPool(mapKind: MapKind, islandSize: number): readonly CuratedSeed[] {
  const exact = MAP_SEEDS[`${mapKind}/${islandSize}`];
  if (exact) return exact;
  const sizes = [8, 10, 12];
  const nearest = sizes.reduce((a, b) => (Math.abs(b - islandSize) < Math.abs(a - islandSize) ? b : a));
  return MAP_SEEDS[`${mapKind}/${nearest}`] ?? [];
}

/** Draw one curated board for a fresh match. `roll` is the caller's nondeterminism (Math.random in the
 *  UI, the host's roll in MP): the ENGINE stays seed-deterministic, only the pick is random. */
export function pickMapSeed(mapKind: MapKind, islandSize: number, roll: number): CuratedSeed {
  const pool = seedPool(mapKind, islandSize);
  if (!pool.length) return { seed: (roll * 0x7fffffff) >>> 0, name: 'Uncharted', blurb: 'A wild roll of the dice.' };
  return pool[Math.floor(Math.max(0, Math.min(0.999999, roll)) * pool.length)]!;
}
